
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { LayoutService } from '../../../projects/components/src/public-api';
import { PROMPTS, Prompt, PromptField } from '../data/prompt.data';
import { NEXT_PROMPTS, NextPromptItem } from '../data/next-prompt.data';

interface SavedPromptConfig {
  saveName: string;
  promptName: string;
  fields: Record<string, string>;
  sourceMaterial: string;
  activeAddOns: string[];
  addOnFields: Record<string, Record<string, string>>;
  savedAt: number;
}

@Component({
  selector: 'contribute',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: 'builder.component.html',
  styleUrl: 'builder.component.css',
})
export class BuilderComponent implements OnInit {
  constructor(private layout: LayoutService) {}

  // ============================================================
  // Static data
  // ============================================================
  readonly PROMPTS = PROMPTS;
  readonly NEXT_PROMPTS = NEXT_PROMPTS;

  // ============================================================
  // Core prompt selection
  // ============================================================
  selectedPromptName: string = '';
  selectedPrompt?: Prompt;
  currentPrompt: Prompt | null = null;

  groupedPrompts: { label: string; items: Prompt[] }[] = [];
  nextPrompts: NextPromptItem[] = [];

  // ============================================================
  // Main prompt field values
  // ============================================================
  fieldValues: Record<string, string> = {};
  sourceMaterial: string = '';

  // ============================================================
  // Add-ons
  // ============================================================
  activeAddOns: string[] = [];
  addOnFieldValues: Record<string, Record<string, any> | undefined> = {};

  // ============================================================
  // Stars / saved configs
  // ============================================================
  starredPrompts: string[] = [];
  savedConfigs: SavedPromptConfig[] = [];
  savedListOpen = false;
  saveNameInput = '';

  // ============================================================
  // UI visibility / labels
  // ============================================================
  descVisible = false;
  sourceVisible = false;
  nextVisible = false;
  saveVisible = false;
  savedVisible = false;

  outputSublabel = 'Select a prompt type to begin building your prompt.';

  // ============================================================
  // Output panel
  // ============================================================
  outputText = '';
  charCountText = '';
  copyButtonText = 'Copy Prompt';
  copyButtonDisabled = true;

  // ============================================================
  // Progress / status
  // ============================================================
  progressVisible = false;
  progressPercent = 0;
  progressLabel = '';

  statusVisible = false;
  isComplete = false;
  statusText = '';

  // ============================================================
  // Grouping
  // ============================================================
  /*
  groupMeta: Record<string, string> = {
    plan: 'Plan & Strategize',
    draft: 'Draft & Write',
    prepare: 'Prepare & Present',
    analyze: 'Analyze & Advise'
  };
  */
  groupMeta: Record<string, string> = {
    Plan: 'Plan & Strategize',
    Draft: 'Draft & Write',
    Prepare: 'Prepare & Present',
    Analyze: 'Analyze & Advise',
    Compliance: 'Compliance'
  };
  //groups = ['plan', 'draft', 'prepare', 'analyze'];
  groups = ['Plan', 'Draft', 'Prepare', 'Analyze', 'Compliance'];

  // ============================================================
  // Lifecycle
  // ============================================================
  ngOnInit(): void {
    this.layout.setClass('layout-12');

    this.starredPrompts = this.loadStarredPrompts();
    this.savedConfigs = this.loadSavedConfigs();

    this.buildGroupedPrompts();
    this.updateSavedVisibility();
    this.loadFromURL();
  }

  // ============================================================
  // Local storage
  // ============================================================
  private loadStarredPrompts(): string[] {
    try {
      return JSON.parse(localStorage.getItem('pb4_starred') || '[]');
    } catch {
      return [];
    }
  }

  private saveStarredPrompts(): void {
    localStorage.setItem('pb4_starred', JSON.stringify(this.starredPrompts));
  }

  private loadSavedConfigs(): SavedPromptConfig[] {
    try {
      return JSON.parse(localStorage.getItem('pb4_saved') || '[]');
    } catch {
      return [];
    }
  }

  private saveSavedConfigs(): void {
    localStorage.setItem('pb4_saved', JSON.stringify(this.savedConfigs));
  }

  // ============================================================
  // Dropdown grouping
  // ============================================================
  buildGroupedPrompts(): void {
    const result: { label: string; items: Prompt[] }[] = [];

    const starred = this.PROMPTS.filter(p =>
      this.starredPrompts.includes(p.name)
    );

    if (starred.length > 0) {
      result.push({
        label: '★ Starred',
        items: starred
      });
    }

    this.groups.forEach(g => {
      const groupItems = this.PROMPTS.filter(p => p.group === g);

      if (groupItems.length > 0) {
        result.push({
          label: this.groupMeta[g],
          items: groupItems
        });
      }
    });

    this.groupedPrompts = result;
  }

  // ============================================================
  // Prompt selection
  // ============================================================
  onCategoryChange(): void {
    const name = this.selectedPromptName;

    if (!name) {
      this.showEmptyState();
      return;
    }

    this.currentPrompt = this.PROMPTS.find(p => p.name === name) || null;
    this.selectedPrompt = this.currentPrompt || undefined;

    if (!this.currentPrompt) {
      this.showEmptyState();
      return;
    }

    // Reset add-ons for new prompt
    this.activeAddOns = [];
    this.addOnFieldValues = {};

    // Initialize main fields with defaults
    this.initializeMainFieldValues();

    // Load next-step suggestions
    this.nextPrompts = this.NEXT_PROMPTS[this.currentPrompt.name] || [];

    // Show sections
    this.descVisible = true;
    this.sourceVisible = true;
    this.nextVisible = this.nextPrompts.length > 0;
    this.saveVisible = true;

    this.updateOutput();
  }

  private initializeMainFieldValues(): void {
    this.fieldValues = {};

    if (!this.currentPrompt) return;

    this.currentPrompt.fields.forEach(field => {
      this.fieldValues[field.id] = field.default ?? '';
    });
  }

  showEmptyState(): void {
    this.selectedPrompt = undefined;
    this.currentPrompt = null;
    this.nextPrompts = [];
    this.activeAddOns = [];
    this.addOnFieldValues = {};
    this.fieldValues = {};
    this.sourceMaterial = '';

    this.descVisible = false;
    this.sourceVisible = false;
    this.nextVisible = false;
    this.saveVisible = false;

    this.outputText = '';
    this.charCountText = '';
    this.copyButtonDisabled = true;

    this.progressVisible = false;
    this.progressPercent = 0;
    this.progressLabel = '';

    this.statusVisible = false;
    this.isComplete = false;
    this.statusText = '';

    this.outputSublabel = 'Select a prompt type to begin building your prompt.';
  }

  // ============================================================
  // Main field handling
  // ============================================================
  onMainFieldChange(fieldId: string, value: string): void {
    this.fieldValues[fieldId] = value;
    this.updateOutput();
  }

  onSourceInput(): void {
    this.updateOutput();
  }

  isFieldFilled(fieldId: string): boolean {
    return !!(this.fieldValues[fieldId] || '').trim();
  }

  // ============================================================
  // Add-ons / next prompts
  // ============================================================
  toggleAddOn(name: string): void {
    if (this.activeAddOns.includes(name)) {
      this.removeAddOn(name);
    } else {
      this.activateAddOn(name);
    }
  }

  activateAddOn(name: string): void {
    if (this.activeAddOns.includes(name)) return;

    this.activeAddOns.push(name);

    const addOnPrompt = this.PROMPTS.find(p => p.name === name);
    if (!addOnPrompt) {
      this.updateOutput();
      return;
    }

    const values: Record<string, string> = {};
    addOnPrompt.fields.forEach(field => {
      values[field.id] = field.default ?? '';
    });

    this.addOnFieldValues[name] = values;
    this.updateOutput();
  }

  removeAddOn(name: string): void {
    this.activeAddOns = this.activeAddOns.filter(n => n !== name);
    delete this.addOnFieldValues[name];
    this.updateOutput();
  }

  isAddOnActive(name: string): boolean {
    return this.activeAddOns.includes(name);
  }

  getAddOnConfig(name: string): NextPromptItem | undefined {
    if (!this.currentPrompt) return undefined;
    const list = this.NEXT_PROMPTS[this.currentPrompt.name] || [];
    return list.find(n => n.name === name);
  }

  getAddOnPrompt(name: string): Prompt | undefined {
    return this.PROMPTS.find(p => p.name === name);
  }

  getAddOnFields(name: string): PromptField[] {
    const config = this.getAddOnConfig(name);
    const prompt = this.getAddOnPrompt(name);

    if (!config || !prompt) return [];

    return prompt.fields.filter(f => config.addonFieldIds.includes(f.id));
  }

  onAddOnFieldChange(addOnName: string, fieldId: string, value: string): void {
    if (!this.addOnFieldValues[addOnName]) {
      this.addOnFieldValues[addOnName] = {};
    }
    this.addOnFieldValues[addOnName][fieldId] = value;
    this.updateOutput();
  }

  isAddOnFieldFilled(addOnName: string, fieldId: string): boolean {
    return !!(this.addOnFieldValues[addOnName]?.[fieldId] || '').trim();
  }

  // ============================================================
  // Star handling
  // ============================================================
  get isCurrentPromptStarred(): boolean {
    return !!this.currentPrompt && this.starredPrompts.includes(this.currentPrompt.name);
  }

  toggleStar(): void {
    if (!this.currentPrompt) return;

    const name = this.currentPrompt.name;
    const idx = this.starredPrompts.indexOf(name);

    if (idx >= 0) {
      this.starredPrompts.splice(idx, 1);
    } else {
      this.starredPrompts.push(name);
    }

    this.saveStarredPrompts();
    this.buildGroupedPrompts();
  }

  // ============================================================
  // Output generation
  // ============================================================
  generatePrompt(): string {
    if (!this.currentPrompt) return '';

    let text = this.currentPrompt.template;

    // Replace main prompt placeholders
    this.currentPrompt.fields.forEach(field => {
      const val = (this.fieldValues[field.id] || '').trim();
      text = text.split(`[${field.id}]`).join(val || `[${field.id}]`);
    });

    // Replace source material
    text = text
      .split('[SOURCE_MATERIAL]')
      .join(this.sourceMaterial.trim() || '[No source material provided]');

    // Append active add-ons
    if (this.activeAddOns.length > 0) {
      this.activeAddOns.forEach(addOnName => {
        const addOnConfig = this.getAddOnConfig(addOnName);
        if (!addOnConfig?.addonInstruction) return;

        let addOnText = addOnConfig.addonInstruction;

        (addOnConfig.addonFieldIds || []).forEach(fieldId => {
          const val = (this.addOnFieldValues[addOnName]?.[fieldId] || '').trim();
          addOnText = addOnText.split(`[${fieldId}]`).join(val || `[${fieldId}]`);
        });

        text +=
          '\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n' +
          'Additionally, using the same context, source material, role, and tone above:\n\n' +
          addOnText;
      });
    }

    return text;
  }

  getFieldStats(): { filled: number; total: number } {
    if (!this.currentPrompt) return { filled: 0, total: 0 };

    let filled = 0;
    let total = 0;

    this.currentPrompt.fields.forEach(field => {
      if (!field.required) return;
      total++;

      const val = (this.fieldValues[field.id] || '').trim();
      if (val) filled++;
    });

    return { filled, total };
  }

  updateOutput(): void {
    if (!this.currentPrompt) {
      this.outputText = '';
      this.charCountText = '';
      this.copyButtonDisabled = true;
      this.progressVisible = false;
      this.statusVisible = false;
      return;
    }

    const promptText = this.generatePrompt();

    this.outputText = promptText;
    this.charCountText = `${promptText.length.toLocaleString()} characters`;
    this.copyButtonDisabled = false;

    const { filled, total } = this.getFieldStats();

    this.progressVisible = true;
    this.progressPercent = total > 0 ? Math.round((filled / total) * 100) : 100;
    this.progressLabel = `${filled} of ${total} required fields completed`;

    this.isComplete = filled === total;
    this.statusVisible = true;
    this.statusText = this.isComplete
      ? 'All required fields complete — Your prompt is ready to use.'
      : `${total - filled} required field${total - filled !== 1 ? 's' : ''} remaining.`;
  }

  // ============================================================
  // Copy
  // ============================================================
  async copyPrompt(): Promise<void> {
    if (!this.outputText) return;

    try {
      await navigator.clipboard.writeText(this.outputText);
      this.copyButtonText = 'Copied!';
      setTimeout(() => {
        this.copyButtonText = 'Copy Prompt';
      }, 2000);
    } catch {
      this.copyButtonText = 'Copied!';
      setTimeout(() => {
        this.copyButtonText = 'Copy Prompt';
      }, 2000);
    }
  }

  // ============================================================
  // Save / load config
  // ============================================================
  private getConfigState(): Omit<SavedPromptConfig, 'saveName' | 'savedAt'> | null {
    if (!this.currentPrompt) return null;

    return {
      promptName: this.currentPrompt.name,
      fields: { ...this.fieldValues },
      sourceMaterial: this.sourceMaterial,
      activeAddOns: [...this.activeAddOns],
      addOnFields: JSON.parse(JSON.stringify(this.addOnFieldValues))
    };
  }

  saveConfig(): void {
    const name = this.saveNameInput.trim();
    if (!name) return;

    const state = this.getConfigState();
    if (!state) return;

    const existing = this.savedConfigs.findIndex(c => c.saveName === name);

    const entry: SavedPromptConfig = {
      saveName: name,
      ...state,
      savedAt: Date.now()
    };

    if (existing >= 0) {
      this.savedConfigs[existing] = entry;
    } else {
      this.savedConfigs.unshift(entry);
    }

    this.saveSavedConfigs();
    this.saveNameInput = '';
    this.updateSavedVisibility();
  }

  loadConfig(idx: number): void {
    const config = this.savedConfigs[idx];
    if (!config) return;

    this.selectedPromptName = config.promptName;
    this.onCategoryChange();

    this.fieldValues = { ...(config.fields || {}) };
    this.sourceMaterial = config.sourceMaterial || '';
    this.activeAddOns = [...(config.activeAddOns || [])];
    this.addOnFieldValues = JSON.parse(JSON.stringify(config.addOnFields || {}));

    this.nextPrompts = this.currentPrompt
      ? this.NEXT_PROMPTS[this.currentPrompt.name] || []
      : [];

    this.updateOutput();
  }

  deleteConfig(idx: number): void {
    this.savedConfigs.splice(idx, 1);
    this.saveSavedConfigs();
    this.updateSavedVisibility();
  }

  showSavedList = false;

  toggleSavedList(): void {
    this.savedListOpen = !this.savedListOpen;
    this.showSavedList = !this.showSavedList;
  }

  private updateSavedVisibility(): void {
    this.savedVisible = this.savedConfigs.length > 0;
  }

  // ============================================================
  // Share via URL
  // ============================================================
  async shareConfig(): Promise<void> {
    const state = this.getConfigState();
    if (!state) return;

    const encoded = btoa(encodeURIComponent(JSON.stringify(state)));
    const url = window.location.href.split('?')[0] + '?config=' + encoded;

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy this link to share your configuration:', url);
    }
  }

  loadFromURL(): void {
    const params = new URLSearchParams(window.location.search);
    const configParam = params.get('config');
    if (!configParam) return;

    try {
      const state = JSON.parse(decodeURIComponent(atob(configParam)));

      if (state.promptName) {
        this.selectedPromptName = state.promptName;
        this.onCategoryChange();
      }

      this.fieldValues = { ...(state.fields || {}) };
      this.sourceMaterial = state.sourceMaterial || '';
      this.activeAddOns = [...(state.activeAddOns || [])];
      this.addOnFieldValues = JSON.parse(JSON.stringify(state.addOnFields || {}));

      this.updateOutput();
    } catch {
      // ignore bad URL config
    }
  }

  // ============================================================
  // Template helpers
  // ============================================================
  get hasPromptSelected(): boolean {
    return !!this.currentPrompt;
  }

  get currentCrumb(): string {
    if (!this.currentPrompt) return '';
    return `${this.groupMeta[this.currentPrompt.group]} / ${this.currentPrompt.name}`;
  }
}